import { RecordCollection } from "./records";
import { RECORDS_PER_PAGE } from "@taunggyi-plus/domain";
export { RECORDS_PER_PAGE } from "@taunggyi-plus/domain";
import {
  useState,
  useEffect,
  useRef,
  useId,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
} from "react";

export function LinkWordmark() {
  return (
    <span className="brand-wordmark__link" aria-label="LINK">
      <i>L</i>
      <i>I</i>
      <i>N</i>
      <i>K</i>
    </span>
  );
}

export function BrandLogo({ compact = false }: { readonly compact?: boolean }) {
  return (
    <span className="brand-lockup" aria-label="TaungGyi Link">
      <span className="brand-mark" aria-hidden="true">
        <img src="/taunggyi-link-official-mark.png" alt="" />
      </span>
      {!compact && (
        <span className="brand-wordmark">
          <strong>TAUNGGYI</strong>
          <LinkWordmark />
        </span>
      )}
    </span>
  );
}

export function Button({ className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`button ${className}`.trim()} {...props} />;
}

export function Card({
  children,
  className = "",
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLElement>>) {
  return (
    <section className={`card ${className}`.trim()} {...props}>
      {children}
    </section>
  );
}

export function PageTitle({ title }: { readonly title: ReactNode }) {
  return (
    <header className="page-title">
      <h1>{title}</h1>
    </header>
  );
}

export function StatusPill({
  children,
  tone = "neutral",
}: PropsWithChildren<{ readonly tone?: "neutral" | "success" | "warning" }>) {
  return <span className={`status-pill status-pill--${tone}`}>{children}</span>;
}

export function EmptyState({
  icon,
  title,
  description,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon" aria-hidden="true">
        {icon}
      </span>
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}

export interface ListFilterOption {
  readonly value: string;
  readonly label: string;
}

export interface ListFilter {
  readonly label: string;
  readonly value: string;
  readonly options: readonly ListFilterOption[];
  readonly onChange: (value: string) => void;
}

export function ManagementListToolbar({
  query,
  onQueryChange,
  searchLabel = "Search list",
  filters = [],
  resultCount,
  onClear,
  actions,
}: {
  readonly query: string;
  readonly onQueryChange: (value: string) => void;
  readonly searchLabel?: string;
  readonly filters?: readonly ListFilter[];
  readonly resultCount: number;
  readonly onClear: () => void;
  readonly actions?: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [draft, setDraft] = useState<string[]>([]);
  const [draftQuery, setDraftQuery] = useState("");
  const filtered = Boolean(query.trim() || filters.some((filter) => filter.value !== "all"));
  return (
    <div className="management-toolbar">
      <div className="management-toolbar__summary" aria-live="polite">
        <span>
          <strong>{resultCount}</strong> {resultCount === 1 ? "record" : "records"}
        </span>
        {filtered && (
          <button type="button" onClick={onClear}>
            Clear filters
          </button>
        )}
      </div>
      <div className="management-toolbar__actions">
        {actions}
        <button
          type="button"
          className="toolbar-icon-button"
          aria-label="Filter list"
          title="Filter list"
          aria-haspopup="dialog"
          data-filtered={filtered}
          onClick={() => {
            setDraftQuery(query);
            setDraft(filters.map((filter) => filter.value));
            dialog.current?.showModal();
          }}
        >
          <ActionIcon action="filter" />
        </button>
      </div>
      <dialog
        ref={dialog}
        className="record-modal customer-order-filter-dialog subscription-popup"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            dialog.current?.close();
          }
        }}
        aria-labelledby={titleId}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onQueryChange(draftQuery);
            filters.forEach((filter, index) => filter.onChange(draft[index] ?? filter.value));
            dialog.current?.close();
          }}
        >
          <header>
            <h2 id={titleId}>Filters</h2>
            <button
              type="button"
              className="toolbar-icon-button"
              aria-label="Close filters"
              onClick={() => dialog.current?.close()}
            >
              ×
            </button>
          </header>
          <label className="field">
            <span>{searchLabel}</span>
            <input
              type="search"
              value={draftQuery}
              placeholder={searchLabel}
              onChange={(event) => setDraftQuery(event.target.value)}
            />
          </label>
          {filters.map((filter, index) => (
            <label className="field" key={filter.label}>
              <span>{filter.label}</span>
              <select
                value={draft[index] ?? filter.value}
                onChange={(event) =>
                  setDraft((values) =>
                    values.map((value, i) => (i === index ? event.target.value : value)),
                  )
                }
              >
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <footer className="subscription-popup-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={() => {
                setDraftQuery("");
                setDraft(
                  filters.map(
                    (filter) =>
                      filter.options.find((option) => option.value === "all")?.value ??
                      filter.options[0]?.value ??
                      filter.value,
                  ),
                );
              }}
            >
              Reset
            </button>
            <button className="button" type="submit">
              Apply filters
            </button>
          </footer>
        </form>
      </dialog>
    </div>
  );
}

export function PaginatedList<T>({
  items,
  renderItem,
  resetKey = "",
  pageSize = RECORDS_PER_PAGE,
  label = "List pages",
  layout = "table",
  pagination = "local",
}: {
  readonly items: readonly T[];
  readonly renderItem: (item: T, index: number) => ReactNode;
  readonly resetKey?: string;
  readonly pageSize?: number;
  readonly label?: string;
  readonly layout?: "table" | "cards";
  /** External pagination owns fetching, visible records, and the loading control. */
  readonly pagination?: "local" | "external";
}) {
  return (
    <ListPage
      key={resetKey}
      items={items}
      renderItem={renderItem}
      pageSize={pageSize}
      label={label}
      layout={layout}
      pagination={pagination}
    />
  );
}

function ListPage<T>({
  items,
  renderItem,
  pageSize,
  label,
  layout,
  pagination,
}: {
  readonly items: readonly T[];
  readonly renderItem: (item: T, index: number) => ReactNode;
  readonly pageSize: number;
  readonly label: string;
  readonly layout: "table" | "cards";
  readonly pagination: "local" | "external";
}) {
  const [page, setPage] = useState(0);
  const size = Math.max(1, Math.floor(pageSize));
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(page, pages - 1);
  const selectedDetail =
    new URLSearchParams(window.location.search).get("list") === label &&
    new URLSearchParams(window.location.search).has("record");
  const visible =
    selectedDetail || pagination === "external"
      ? items.length
      : Math.min((current + 1) * size, items.length);
  return (
    <>
      {layout === "table" ? (
        <RecordCollection label={label} records={items.slice(0, visible).map(renderItem)} />
      ) : (
        items.slice(0, visible).map(renderItem)
      )}
      {pagination === "local" && !selectedDetail && items.length > 0 && (
        <InfiniteLoader
          hasMore={visible < items.length}
          loading={false}
          onLoad={() => setPage(current + 1)}
          label={`${visible} of ${items.length}`}
        />
      )}
    </>
  );
}

export function InfiniteLoader({
  hasMore,
  loading = false,
  onLoad,
  label,
}: {
  hasMore: boolean;
  loading?: boolean;
  onLoad: () => void;
  label?: string;
}) {
  const marker = useRef<HTMLDivElement>(null);
  const callback = useRef(onLoad);
  useEffect(() => {
    callback.current = onLoad;
  });
  useEffect(() => {
    if (!hasMore || loading || !marker.current || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          callback.current();
        }
      },
      { rootMargin: "120px", threshold: 0.1 },
    );
    observer.observe(marker.current);
    return () => observer.disconnect();
  }, [hasMore, loading, label]);
  return (
    <div ref={marker} className="list-pagination infinite-loader">
      <span aria-live="polite">{label}</span>
      {hasMore && (
        <button className="button button--secondary" disabled={loading} onClick={onLoad}>
          {loading ? "Loading…" : "Load 10 more"}
        </button>
      )}
    </div>
  );
}
export function ServerPagination({
  page,
  total,
  onPage,
  loading = false,
}: {
  page: number;
  total: number;
  onPage: (page: number) => void;
  loading?: boolean;
}) {
  return (
    <InfiniteLoader
      hasMore={(page + 1) * RECORDS_PER_PAGE < total}
      loading={loading}
      onLoad={() => onPage(page + 1)}
      label={`${Math.min(total, (page + 1) * RECORDS_PER_PAGE)} of ${total}`}
    />
  );
}

export function ActionIcon({ action }: { readonly action: "add" | "filter" | "refresh" | "edit" }) {
  return (
    <svg
      className="action-icon"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      {action === "add" ? (
        <path d="M12 5v14M5 12h14" />
      ) : action === "edit" ? (
        <path d="m15 4 5 5M4 20l5-1L21 7a2.8 2.8 0 0 0-4-4L5 15z" />
      ) : action === "refresh" ? (
        <>
          <path d="M20 7v5h-5" />
          <path d="M19.1 7A8 8 0 1 0 20 15" />
        </>
      ) : (
        <>
          <path d="M4 7h16M4 17h16" />
          <circle cx="9" cy="7" r="3" fill="var(--surface)" />
          <circle cx="15" cy="17" r="3" fill="var(--surface)" />
        </>
      )}
    </svg>
  );
}

/** A consistent, keyboard-accessible record preview and detail popup. */
export function DetailCard({
  title,
  summary,
  children,
  className = "",
}: PropsWithChildren<{
  readonly title: string;
  readonly summary?: ReactNode;
  readonly className?: string;
}>) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  return (
    <Card className={className}>
      <div>
        <h3>{title}</h3>
        {summary}
      </div>
      <button
        type="button"
        className="button button--secondary"
        aria-haspopup="dialog"
        onClick={() => dialog.current?.showModal()}
      >
        View details
      </button>
      <dialog
        ref={dialog}
        className="customer-order-filter-dialog subscription-popup record-detail-popup"
        aria-labelledby={titleId}
      >
        <header>
          <h3 id={titleId}>{title}</h3>
          <button
            type="button"
            className="toolbar-icon-button"
            aria-label="Close details"
            onClick={() => dialog.current?.close()}
          >
            ×
          </button>
        </header>
        <div className="record-detail-popup__content">{children}</div>
      </dialog>
    </Card>
  );
}

/** Shared language selector glyph for login, headers, and account menus. */
export function GlobeIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.1 2.25 3.2 5.1 3.2 8.5s-1.1 6.25-3.2 8.5c-2.1-2.25-3.2-5.1-3.2-8.5S9.9 5.75 12 3.5" />
    </svg>
  );
}
